import React, { useState, useEffect, useCallback } from "react";

import {
  listFileSources,
  createFileSource,
  updateFileSource,
  deleteFileSource,
  testFileSource,
  syncFileSource,
  resolveFileSourceLibraries,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import PasswordInput from "../../../../components/PasswordInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";

// Mirrors filesource.MinSyncIntervalSeconds / DefaultSyncIntervalSeconds.
const MIN_SYNC_MINUTES = 5;
const DEFAULT_SYNC_MINUTES = 15;

const errorMessage = (err, fb) => String(err?.message || err).trim() || fb;

const relativeTime = (iso) => {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

// One line per source describing where it stands, so the list answers "is my
// data fresh" without opening anything.
const statusLine = (source) => {
  if (!source.enabled) return "Disabled";
  if (source.last_error) return `Failed: ${source.last_error}`;
  const when = relativeTime(source.last_sync_at);
  if (!when) return "Waiting for first sync";
  const added = source.last_result?.added;
  return added ? `Synced ${when} · ${added} new` : `Synced ${when}`;
};

const INITIAL = {
  name: "",
  tenantId: "",
  clientId: "",
  clientSecret: "",
  siteUrl: "",
  driveId: "",
  driveName: "",
  pathPrefix: "",
  syncMinutes: DEFAULT_SYNC_MINUTES,
  enabled: true,
};

const SourceModal = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (editing
    ? {
      ...INITIAL,
      name: editing.name || "",
      tenantId: editing.tenant_id || "",
      clientId: editing.client_id || "",
      driveId: editing.drive_id || "",
      pathPrefix: editing.path_prefix || "",
      syncMinutes: Math.round((editing.sync_interval_seconds || 900) / 60),
      enabled: !!editing.enabled,
    }
    : INITIAL));
  const [libraries, setLibraries] = useState([]);
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;

  const set = (field) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // The drive id is immutable server-side, so editing keeps the saved library
  // rather than offering a picker that could only fail with a 409.
  const findLibraries = async () => {
    setFinding(true);
    setFindError("");
    setLibraries([]);
    try {
      const result = await resolveFileSourceLibraries({
        tenant_id: form.tenantId.trim(),
        client_id: form.clientId.trim(),
        client_secret: form.clientSecret,
        site_url: form.siteUrl.trim(),
      });
      if (result?.error) {
        setFindError(result.error);
        return;
      }
      const found = Array.isArray(result?.libraries) ? result.libraries : [];
      if (found.length === 0) {
        setFindError("That site has no document libraries this app can read.");
        return;
      }
      setLibraries(found);
      setForm((prev) => ({
        ...prev,
        driveId: prev.driveId || found[0].drive_id,
        driveName: found[0].name || "",
      }));
    } catch (err) {
      setFindError(errorMessage(err, "Could not reach the site."));
    } finally {
      setFinding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const interval = Math.max(MIN_SYNC_MINUTES, Number(form.syncMinutes) || DEFAULT_SYNC_MINUTES) * 60;
    try {
      if (isEdit) {
        const patch = {
          name: form.name.trim(),
          tenant_id: form.tenantId.trim(),
          client_id: form.clientId.trim(),
          path_prefix: form.pathPrefix.trim(),
          sync_interval_seconds: interval,
          enabled: form.enabled,
        };
        // Omitted leaves the stored secret alone; the placeholder says so.
        if (form.clientSecret) patch.client_secret = form.clientSecret;
        await updateFileSource(editing.id, patch);
      } else {
        await createFileSource({
          name: form.name.trim(),
          provider: "sharepoint",
          tenant_id: form.tenantId.trim(),
          client_id: form.clientId.trim(),
          client_secret: form.clientSecret,
          drive_id: form.driveId.trim(),
          path_prefix: form.pathPrefix.trim(),
          sync_interval_seconds: interval,
          enabled: form.enabled,
        });
      }
      notify({
        type: "success",
        title: isEdit ? "Updated" : "Created",
        message: `Document source "${form.name.trim() || form.driveId}" ${isEdit ? "updated" : "created"}.`,
      });
      onSaved();
      onClose();
    } catch (err) {
      notify({
        type: "error",
        title: isEdit ? "Update Failed" : "Create Failed",
        message: errorMessage(err, "Unknown error"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canFind = !!(form.tenantId.trim() && form.clientId.trim() && form.clientSecret && form.siteUrl.trim());

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 560 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name || editing.id}` : "New Document Source"}</div>
            <div className="gwModalSubtitle">
              {isEdit
                ? "Update the source configuration."
                : "Sync a SharePoint document library so its files can be searched and cited."}
            </div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input id="fs-name" className="gwInput" placeholder="e.g. Maintenance manuals" value={form.name} onChange={set("name")} autoFocus />
          </div>

          <div className="gwSectionTitle" style={{ marginTop: 4 }}>Microsoft Entra app</div>
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Directory (tenant) ID</label>
              <input id="fs-tenant-id" className="gwInput" value={form.tenantId} onChange={set("tenantId")} required />
            </div>
            <div className="gwField">
              <label className="gwLabel">Application (client) ID</label>
              <input id="fs-client-id" className="gwInput" value={form.clientId} onChange={set("clientId")} required />
            </div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Client secret</label>
            <PasswordInput
              id="fs-client-secret"
              placeholder={isEdit && editing.has_secret ? "Leave blank to keep current" : ""}
              value={form.clientSecret}
              onChange={set("clientSecret")}
            />
            <div className="gwHint">
              App-only credentials. Grant the app <code>Sites.Selected</code>, then grant it read on this one site.
            </div>
          </div>

          <div className="gwSectionTitle" style={{ marginTop: 4 }}>Library</div>
          {isEdit ? (
            <div className="gwField">
              <label className="gwLabel">Drive ID</label>
              <input className="gwInput" value={form.driveId} disabled />
              <div className="gwHint">The library cannot be changed. Create a new source to sync a different one.</div>
            </div>
          ) : (
            <>
              <div className="gwField">
                <label className="gwLabel">Site URL</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="fs-site-url"
                    className="gwInput"
                    placeholder="https://acme.sharepoint.com/sites/Maintenance"
                    value={form.siteUrl}
                    onChange={set("siteUrl")}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="gwGhostBtn"
                    type="button"
                    onClick={findLibraries}
                    disabled={!canFind || finding}
                    style={{ flexShrink: 0 }}
                  >
                    {finding ? "Finding..." : "Find libraries"}
                  </button>
                </div>
                <div className="gwHint">Paste the site address from your browser. Fill in the app fields first.</div>
                {findError && (
                  <div className="gwHint" style={{ color: "var(--gw-danger)" }}>{findError}</div>
                )}
              </div>
              {libraries.length > 0 && (
                <div className="gwField">
                  <label className="gwLabel">Library</label>
                  {libraries.map((library) => (
                    <label
                      key={library.drive_id}
                      style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, padding: "3px 0" }}
                    >
                      <input
                        type="radio"
                        name="library"
                        checked={form.driveId === library.drive_id}
                        onChange={() => setForm((p) => ({ ...p, driveId: library.drive_id, driveName: library.name || "" }))}
                      />
                      <span>{library.name || library.drive_id}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="gwField">
            <label className="gwLabel">Folder path (optional)</label>
            <input id="fs-path-prefix" className="gwInput" placeholder="/Pumps/Manuals" value={form.pathPrefix} onChange={set("pathPrefix")} />
            <div className="gwHint">Limits the sync to one folder subtree. Blank syncs the whole library.</div>
          </div>

          <div className="gwField">
            <label className="gwLabel">Check for changes every</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                id="fs-sync-minutes"
                className="gwInput"
                type="number"
                min={MIN_SYNC_MINUTES}
                value={form.syncMinutes}
                onChange={set("syncMinutes")}
                style={{ width: 90 }}
              />
              <span style={{ fontSize: 13, color: "var(--gw-text-secondary)" }}>minutes</span>
            </div>
            <div className="gwHint">Minimum {MIN_SYNC_MINUTES} minutes. New and changed files are picked up; nothing is written back.</div>
          </div>
          <div className="gwField">
            <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
              <input
                id="fs-enabled"
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
              />
              <span>Enabled</span>
            </label>
          </div>
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting || (!isEdit && !form.driveId)}>
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Source"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const FileSourcesPage = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const canConfigure = usePermission("configure");
  const notify = useNotify();

  const fetchAll = useCallback(async () => {
    try {
      setSources(await listFileSources());
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Failed to load document sources") });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: `${Math.round(r.bottom + 4)}px`,
      right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px`,
    });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (document.querySelector(".gwProjectMenu")?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  // /test answers 200 with {ok, error}, so a rejected credential arrives as data.
  const runTest = async (source) => {
    setBusy(source.id);
    try {
      const result = await testFileSource(source.id);
      if (result?.ok) {
        notify({ type: "success", title: "Connection OK", message: `${source.name || source.id} is reachable.` });
      } else {
        notify({ type: "error", title: "Test Failed", message: result?.error || "The source rejected the credentials." });
      }
    } catch (err) {
      notify({ type: "error", title: "Test Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setBusy(null);
    }
  };

  const runSync = async (source) => {
    setBusy(source.id);
    try {
      await syncFileSource(source.id);
      notify({ type: "success", title: "Sync Queued", message: `${source.name || source.id} will sync shortly.` });
    } catch (err) {
      notify({ type: "error", title: "Sync Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteFileSource(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `Source "${deleteTarget.name || deleteTarget.id}" deleted.` });
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  const hasSources = sources.length > 0;
  const addButton = (
    <button
      className="gwPrimaryBtn"
      type="button"
      onClick={() => setShowCreate(true)}
      disabled={!canConfigure}
      title={canConfigure ? undefined : "Requires configure permission"}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
      <span>Add Source</span>
    </button>
  );

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Documents</div>
            <div className="gwPageSubtitle">
              {hasSources
                ? `${sources.length} source${sources.length !== 1 ? "s" : ""} configured`
                : "Sync document libraries so their files can be searched and cited."}
            </div>
          </div>
          {addButton}
        </div>

        {loading && !hasSources && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
            Loading document sources...
          </div>
        )}

        {!loading && !hasSources && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No document sources yet</div>
            <div className="gwEmptyBody">Connect a SharePoint library to make its documents searchable.</div>
            <div className="gwEmptyActions">{addButton}</div>
          </div>
        )}

        {hasSources && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Sources</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {sources.map((source) => (
                <div className="umUserRow" key={source.id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="umUserName">{source.name || source.id}</div>
                    <div className="umUserMeta">
                      SharePoint
                      {source.path_prefix ? ` · ${source.path_prefix}` : ""}
                      {" · "}
                      <span style={source.last_error ? { color: "var(--gw-danger)" } : undefined}>
                        {statusLine(source)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="gwIconButton"
                    style={{ flexShrink: 0 }}
                    onClick={(e) => openMenu(e, source.id)}
                  >
                    <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                  </button>
                  {menuOpen === source.id && (
                    <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="gwProjectMenuItem"
                        type="button"
                        disabled={!canConfigure || busy === source.id}
                        title={canConfigure ? undefined : "Requires configure permission"}
                        onClick={() => { setMenuOpen(null); runTest(source); }}
                      >
                        {busy === source.id ? "Working..." : "Test Connection"}
                      </button>
                      <button
                        className="gwProjectMenuItem"
                        type="button"
                        disabled={!canConfigure || busy === source.id || !source.enabled}
                        title={source.enabled ? undefined : "Enable the source first"}
                        onClick={() => { setMenuOpen(null); runSync(source); }}
                      >
                        Sync Now
                      </button>
                      <button
                        className="gwProjectMenuItem"
                        type="button"
                        disabled={!canConfigure}
                        title={canConfigure ? undefined : "Requires configure permission"}
                        onClick={() => { setMenuOpen(null); setEditing(source); }}
                      >
                        Edit
                      </button>
                      <div className="gwProfileDivider" />
                      <button
                        className="gwProjectMenuItem gwProjectMenuDanger"
                        type="button"
                        disabled={!canConfigure}
                        title={canConfigure ? undefined : "Requires configure permission"}
                        onClick={() => { setMenuOpen(null); setDeleteTarget(source); }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {showCreate && <SourceModal onClose={() => setShowCreate(false)} onSaved={fetchAll} />}
      {editing && <SourceModal editing={editing} onClose={() => setEditing(null)} onSaved={fetchAll} />}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Document Source</div>
                <div className="gwModalSubtitle">Its synced documents are removed too.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove <strong>{deleteTarget.name || deleteTarget.id}</strong>? Everything ingested from this
                library stops being searchable. Nothing in SharePoint is touched.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Source"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
